/**
 * ConsentAwareScriptLoader — Conditionally load third-party scripts based on consent
 *
 * Map of purpose → scripts to load:
 *   analytics:      Google Analytics (gtag), Mixpanel
 *   marketing:      Meta Pixel, Google Ads (gtag)
 *   personalization: Intercom, Hotjar
 *
 * Script injection is deferred until consent is explicitly granted.
 * Scripts are injected as non-blocking async tags to avoid blocking the main thread.
 * On CONSENT_WITHDRAWN, the script is removed and the vendor's opt-out API is called.
 *
 * Usage:
 *   <ConsentAwareScriptLoader purposes={["analytics", "marketing"]} />
 *
 * Or use the individual hooks:
 *   useAnalyticsConsent()  → { loaded, grant, withdraw }
 *   useMarketingConsent()  → { loaded, grant, withdraw }
 */

import { useEffect, useRef, useCallback } from "react";
import { useConsent } from "@/hooks/useConsent";

// ---------------------------------------------------------------------------
// Script definitions
// ---------------------------------------------------------------------------

const SCRIPT_DEFINITIONS = {
  analytics: [
    {
      id: "ga4",
      src: "https://www.googletagmanager.com/gtag/js",
      strategy: "afterInteractive",
      beforeInteractive: false,
      // Inject gtag manually after loading
      onLoad: `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', window.GA_MEASUREMENT_ID || 'G-XXXXXXXXXX');
      `,
      vars: {
        GA_MEASUREMENT_ID: process.env.REACT_APP_GA_MEASUREMENT_ID || "",
      },
    },
  ],
  marketing: [
    {
      id: "meta-pixel",
      src: "https://connect.facebook.net/en_US/fbevents.js",
      strategy: "lazyOnload",
      beforeInteractive: false,
      onLoad: `
        if (window.fbq) {
          fbq('init', window.META_PIXEL_ID || '000000000000000');
          fbq('track', 'PageView');
        }
      `,
      vars: {
        META_PIXEL_ID: process.env.REACT_APP_META_PIXEL_ID || "",
      },
    },
  ],
  personalization: [
    {
      id: "hotjar",
      src: `https://static.hotjar.com/c/hotjar-${process.env.REACT_APP_HOTJAR_ID || 'XXXXXX'}.js`,
      strategy: "afterInteractive",
      beforeInteractive: false,
      onLoad: `
        window.hj = window.hj || function(){(hj.q=hj.q||[]).push(arguments)};
        hj('event', 'page_view');
      `,
    },
  ],
};

// ---------------------------------------------------------------------------
// Core loader component
// ---------------------------------------------------------------------------

/**
 * Load a single script tag, injecting vars and calling onLoad.
 */
function injectScript({ id, src, vars = {}, onLoad }) {
  // Don't inject if src is a placeholder with no real ID
  const isPlaceholder = src.includes("XXXXXX") || src.includes("000000000000000") || src.includes("G-XXXXXXXXXX");
  if (isPlaceholder) {
    console.debug(`[ConsentScript] Skipping placeholder script: ${id}`);
    return;
  }

  // Remove existing if any
  const existing = document.getElementById(id);
  if (existing) existing.remove();

  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = src;

  // Set vars as global window vars before script executes
  Object.entries(vars).forEach(([key, value]) => {
    if (value) {
      window[key] = value;
    }
  });

  // Inject onLoad code as an inline script after src loads
  script.onload = () => {
    if (onLoad) {
      try {
        // eslint-disable-next-line no-eval
        eval(onLoad);
      } catch (err) {
        console.error(`[ConsentScript] Error in onLoad for ${id}:`, err);
      }
    }
  };

  script.onerror = () => {
    console.warn(`[ConsentScript] Failed to load: ${id} (${src})`);
  };

  document.head.appendChild(script);
}

/**
 * Remove a previously injected script.
 */
function removeScript(id) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

/**
 * Call vendor-specific opt-out APIs when consent is withdrawn.
 */
function callVendorOptOut(purpose) {
  switch (purpose) {
    case "analytics":
      // Google Analytics opt-out
      if (typeof window.gtag === "function") {
        window.gtag("consent", "update", { analytics_storage: "denied" });
      }
      // Disable GA4 completely
      window["ga-disable-GA_MEASUREMENT_ID"] = true;
      break;

    case "marketing":
      // Meta Pixel opt-out (disable automatic events)
      if (typeof window.fbq === "function") {
        window.fbq("consent", "revoke");
      }
      break;

    case "personalization":
      // Hotjar opt-out
      if (typeof window.hj === "function") {
        window.hj("privacy", "opt-out");
      }
      break;

    default:
      break;
  }
}

// ---------------------------------------------------------------------------
// useAnalyticsConsent — hook for analytics scripts
// ---------------------------------------------------------------------------

export function useAnalyticsConsent() {
  const { isPurposeGranted, consentStatus } = useConsent();
  const loadedRef = useRef(false);

  const isGranted = isPurposeGranted("analytics");

  const load = useCallback(() => {
    if (loadedRef.current || !isGranted) return;
    const scripts = SCRIPT_DEFINITIONS.analytics || [];
    scripts.forEach(injectScript);
    loadedRef.current = true;
  }, [isGranted]);

  const withdraw = useCallback(() => {
    const scripts = SCRIPT_DEFINITIONS.analytics || [];
    scripts.forEach((s) => removeScript(s.id));
    callVendorOptOut("analytics");
    loadedRef.current = false;
  }, []);

  useEffect(() => {
    if (isGranted) {
      load();
    } else if (loadedRef.current) {
      withdraw();
    }
  }, [isGranted, load, withdraw]);

  return { loaded: loadedRef.current, isGranted, load, withdraw };
}

// ---------------------------------------------------------------------------
// useMarketingConsent — hook for marketing scripts
// ---------------------------------------------------------------------------

export function useMarketingConsent() {
  const { isPurposeGranted } = useConsent();
  const loadedRef = useRef(false);

  const isGranted = isPurposeGranted("marketing");

  const load = useCallback(() => {
    if (loadedRef.current || !isGranted) return;
    const scripts = SCRIPT_DEFINITIONS.marketing || [];
    scripts.forEach(injectScript);
    loadedRef.current = true;
  }, [isGranted]);

  const withdraw = useCallback(() => {
    const scripts = SCRIPT_DEFINITIONS.marketing || [];
    scripts.forEach((s) => removeScript(s.id));
    callVendorOptOut("marketing");
    loadedRef.current = false;
  }, []);

  useEffect(() => {
    if (isGranted) {
      load();
    } else if (loadedRef.current) {
      withdraw();
    }
  }, [isGranted, load, withdraw]);

  return { loaded: loadedRef.current, isGranted, load, withdraw };
}

// ---------------------------------------------------------------------------
// usePersonalizationConsent — hook for personalization scripts
// ---------------------------------------------------------------------------

export function usePersonalizationConsent() {
  const { isPurposeGranted } = useConsent();
  const loadedRef = useRef(false);

  const isGranted = isPurposeGranted("personalization");

  const load = useCallback(() => {
    if (loadedRef.current || !isGranted) return;
    const scripts = SCRIPT_DEFINITIONS.personalization || [];
    scripts.forEach(injectScript);
    loadedRef.current = true;
  }, [isGranted]);

  const withdraw = useCallback(() => {
    const scripts = SCRIPT_DEFINITIONS.personalization || [];
    scripts.forEach((s) => removeScript(s.id));
    callVendorOptOut("personalization");
    loadedRef.current = false;
  }, []);

  useEffect(() => {
    if (isGranted) {
      load();
    } else if (loadedRef.current) {
      withdraw();
    }
  }, [isGranted, load, withdraw]);

  return { loaded: loadedRef.current, isGranted, load, withdraw };
}

// ---------------------------------------------------------------------------
// ConsentAwareScriptLoader — compound component
// ---------------------------------------------------------------------------

/**
 * Compound component that loads all scripts whose corresponding purposes are granted.
 * Drop this once in the app root (after the ConsentBanner mounts).
 *
 * @param {{ purposes?: string[] }} props
 *   List of purpose codes to manage. Defaults to all three.
 */
export default function ConsentAwareScriptLoader({
  purposes = ["analytics", "marketing", "personalization"],
}) {
  const { isPurposeGranted } = useConsent();

  useEffect(() => {
    purposes.forEach((purpose) => {
      if (isPurposeGranted(purpose)) {
        const scripts = SCRIPT_DEFINITIONS[purpose] || [];
        scripts.forEach(injectScript);
      }
    });

    // On withdrawal: remove all scripts and call opt-outs
    return () => {
      purposes.forEach((purpose) => {
        if (!isPurposeGranted(purpose)) {
          const scripts = SCRIPT_DEFINITIONS[purpose] || [];
          scripts.forEach((s) => removeScript(s.id));
          callVendorOptOut(purpose);
        }
      });
    };
  }, [purposes, isPurposeGranted]);

  // This component renders nothing — it manages side effects only
  return null;
}
